import type { IOverlayParticlePreset } from "../../../screens/Dashboard/SubComponents/Studio/interfaces/sub-interfaces/Overlay.interface";

/**
 * Motor de partículas compartido por el carril DOM (`OverlayElementParticles`)
 * y el carril Pixi (`OverlayParticlesRenderer`): mismos presets, misma física
 * (emit → step → draw en canvas2d, dt en unidades de frame a 60 Hz) y misma
 * semántica de `intensity`. Antes cada carril tenía su copia; los presets
 * ambientales (lluvia, nieve, polvo, estrellas, burbujas, nubes) que usan las
 * escenas generadas viven acá una sola vez.
 */
export type ParticleShape =
  | "rect"
  | "circle"
  | "heart"
  | "coin"
  | "spark"
  | "streak"
  | "cloud"
  | "ring";

export interface Particle {
  x: number;
  y: number;
  vx: number;
  vy: number;
  age: number;
  ttl: number;
  rot: number;
  vrot: number;
  size: number;
  hue: number;
  shape: ParticleShape;
  /** Fase aleatoria para twinkle/sway (evita que todas oscilen sincronizadas). */
  phase: number;
}

export interface ParticlePreset {
  spawnRate: number;
  gravity: number;
  drag: number;
  speedMin: number;
  speedMax: number;
  sizeMin: number;
  sizeMax: number;
  ttlMin: number;
  ttlMax: number;
  hueRange: [number, number];
  shape: ParticleShape;
  /** Punto de emisión en espacio local 0..1 (si no hay `spawnArea`). */
  origin: { x: number; y: number };
  /** Cono de dispersión en radianes (PI = abanico de 180°). */
  spread: number;
  /** Dirección inicial en radianes (0 = derecha, PI/2 = abajo). */
  angle: number;
  /** Área de emisión en fracciones 0..1 del elemento (puede exceder para entrar desde afuera). */
  spawnArea?: { x: number; y: number; w: number; h: number };
  /** Vaivén horizontal (px/frame de amplitud) — nieve, polvo, burbujas. */
  sway?: number;
  /** Oscilación de alpha en vez de fade lineal — estrellas. */
  twinkle?: boolean;
  /** Fade-in al nacer y fade-out al morir (evita el "pop" de nubes/polvo). */
  softEdges?: boolean;
  /** Alpha máximo (los ambientales son sutiles). Default 1. */
  alphaMax?: number;
  /** Multiplica el cap de partículas simultáneas (lluvia necesita más, nubes menos). */
  capScale?: number;
  /** Color por defecto cuando el elemento no fija `color` (los ambientales son blancos). */
  defaultColor?: string;
}

const TOP_BAND = { x: -0.1, y: -0.06, w: 1.2, h: 0.05 };
const FULL_AREA = { x: 0, y: 0, w: 1, h: 1 };

export const PRESET_DEFAULTS: Record<IOverlayParticlePreset, ParticlePreset> = {
  confetti: {
    spawnRate: 35,
    gravity: 0.18,
    drag: 0.99,
    speedMin: 2,
    speedMax: 7,
    sizeMin: 6,
    sizeMax: 14,
    ttlMin: 90,
    ttlMax: 180,
    hueRange: [0, 360],
    shape: "rect",
    origin: { x: 0.5, y: 0 },
    spread: Math.PI / 2,
    angle: Math.PI / 2,
  },
  fireworks: {
    spawnRate: 80,
    gravity: 0.05,
    drag: 0.96,
    speedMin: 3,
    speedMax: 9,
    sizeMin: 3,
    sizeMax: 6,
    ttlMin: 40,
    ttlMax: 90,
    hueRange: [0, 360],
    shape: "spark",
    origin: { x: 0.5, y: 0.5 },
    spread: Math.PI * 2,
    angle: 0,
  },
  hearts: {
    spawnRate: 12,
    gravity: -0.1,
    drag: 0.98,
    speedMin: 1,
    speedMax: 3,
    sizeMin: 14,
    sizeMax: 24,
    ttlMin: 100,
    ttlMax: 180,
    hueRange: [340, 360],
    shape: "heart",
    origin: { x: 0.5, y: 1 },
    spread: Math.PI / 4,
    angle: -Math.PI / 2,
  },
  coins: {
    spawnRate: 18,
    gravity: 0.22,
    drag: 0.99,
    speedMin: 3,
    speedMax: 8,
    sizeMin: 14,
    sizeMax: 20,
    ttlMin: 80,
    ttlMax: 160,
    hueRange: [40, 55],
    shape: "coin",
    origin: { x: 0.5, y: 0 },
    spread: Math.PI / 3,
    angle: Math.PI / 2,
  },
  sparkles: {
    spawnRate: 25,
    gravity: 0.02,
    drag: 0.97,
    speedMin: 0.5,
    speedMax: 2,
    sizeMin: 2,
    sizeMax: 5,
    ttlMin: 30,
    ttlMax: 60,
    hueRange: [40, 60],
    shape: "spark",
    origin: { x: 0.5, y: 0.5 },
    spread: Math.PI * 2,
    angle: 0,
  },
  // --- Ambientales (fondos de escena) ---
  rain: {
    spawnRate: 90,
    gravity: 0.15,
    drag: 1,
    speedMin: 9,
    speedMax: 14,
    sizeMin: 12,
    sizeMax: 26,
    ttlMin: 60,
    ttlMax: 140,
    hueRange: [200, 215],
    shape: "streak",
    origin: { x: 0.5, y: 0 },
    spread: 0.12,
    angle: Math.PI / 2 + 0.22,
    spawnArea: { x: -0.15, y: -0.06, w: 1.3, h: 0.05 },
    alphaMax: 0.55,
    capScale: 2.2,
    defaultColor: "#dbeafe",
  },
  snow: {
    spawnRate: 22,
    gravity: 0.012,
    drag: 0.995,
    speedMin: 0.6,
    speedMax: 1.6,
    sizeMin: 3,
    sizeMax: 8,
    ttlMin: 260,
    ttlMax: 480,
    hueRange: [200, 220],
    shape: "circle",
    origin: { x: 0.5, y: 0 },
    spread: 0.4,
    angle: Math.PI / 2,
    spawnArea: TOP_BAND,
    sway: 0.6,
    softEdges: true,
    alphaMax: 0.85,
    defaultColor: "#ffffff",
  },
  dust: {
    spawnRate: 12,
    gravity: -0.004,
    drag: 0.995,
    speedMin: 0.2,
    speedMax: 0.7,
    sizeMin: 2,
    sizeMax: 5,
    ttlMin: 220,
    ttlMax: 420,
    hueRange: [40, 60],
    shape: "spark",
    origin: { x: 0.5, y: 0.5 },
    spread: Math.PI / 2,
    angle: -Math.PI / 2,
    spawnArea: FULL_AREA,
    sway: 0.3,
    softEdges: true,
    alphaMax: 0.55,
    defaultColor: "#ffffff",
  },
  stars: {
    spawnRate: 9,
    gravity: 0,
    drag: 1,
    speedMin: 0,
    speedMax: 0,
    sizeMin: 2,
    sizeMax: 5,
    ttlMin: 220,
    ttlMax: 520,
    hueRange: [40, 60],
    shape: "spark",
    origin: { x: 0.5, y: 0.5 },
    spread: 0,
    angle: 0,
    spawnArea: FULL_AREA,
    twinkle: true,
    alphaMax: 0.9,
    defaultColor: "#ffffff",
  },
  bubbles: {
    spawnRate: 10,
    gravity: -0.012,
    drag: 0.995,
    speedMin: 0.5,
    speedMax: 1.5,
    sizeMin: 6,
    sizeMax: 18,
    ttlMin: 220,
    ttlMax: 380,
    hueRange: [190, 210],
    shape: "ring",
    origin: { x: 0.5, y: 1 },
    spread: 0.5,
    angle: -Math.PI / 2,
    // Banda a caballo del borde inferior: nacen apenas dentro/abajo y suben.
    spawnArea: { x: 0, y: 0.97, w: 1, h: 0.06 },
    sway: 0.8,
    softEdges: true,
    alphaMax: 0.5,
    defaultColor: "#ffffff",
  },
  clouds: {
    spawnRate: 3,
    gravity: 0,
    drag: 1,
    speedMin: 0.2,
    speedMax: 0.5,
    sizeMin: 180,
    sizeMax: 340,
    ttlMin: 700,
    ttlMax: 1100,
    hueRange: [200, 220],
    shape: "cloud",
    origin: { x: 0, y: 0.5 },
    spread: 0.05,
    angle: 0,
    spawnArea: { x: -0.08, y: 0, w: 0.05, h: 1 },
    softEdges: true,
    alphaMax: 0.16,
    capScale: 0.12,
    defaultColor: "#ffffff",
  },
};

/** Cap de partículas simultáneas para una intensidad 0..1 (misma curva que siempre). */
export const particleCap = (preset: ParticlePreset, intensity: number): number =>
  Math.max(1, Math.floor((80 * intensity + 20) * (preset.capScale ?? 1)));

const randomBetween = (min: number, max: number): number => min + Math.random() * (max - min);

/**
 * Emite las partículas que corresponden a este frame (spawnRate × intensity ×
 * dt, con fracción probabilística) hasta el cap. Con `spawnArea` nacen en un
 * punto aleatorio del área; si no, en `origin`.
 */
export const emitParticles = (
  particles: Particle[],
  preset: ParticlePreset,
  intensity: number,
  w: number,
  h: number,
  dt: number
): void => {
  const cap = particleCap(preset, intensity);
  const emitCount = Math.max(
    0,
    Math.floor(preset.spawnRate * intensity * 0.06 * dt + Math.random())
  );
  for (let i = 0; i < emitCount && particles.length < cap; i += 1) {
    const angle = preset.angle + (Math.random() - 0.5) * preset.spread;
    const speed = randomBetween(preset.speedMin, preset.speedMax);
    const area = preset.spawnArea;
    const x = area ? (area.x + Math.random() * area.w) * w : preset.origin.x * w;
    const y = area ? (area.y + Math.random() * area.h) * h : preset.origin.y * h;
    particles.push({
      x,
      y,
      vx: Math.cos(angle) * speed,
      vy: Math.sin(angle) * speed,
      age: 0,
      ttl: randomBetween(preset.ttlMin, preset.ttlMax),
      rot: Math.random() * Math.PI * 2,
      vrot: (Math.random() - 0.5) * 0.3,
      size: randomBetween(preset.sizeMin, preset.sizeMax),
      hue: randomBetween(preset.hueRange[0], preset.hueRange[1]),
      shape: preset.shape,
      phase: Math.random() * Math.PI * 2,
    });
  }
};

/**
 * Cuánto se sale del área (en px) la zona de emisión del preset: las que nacen
 * fuera del borde (nieve arriba, burbujas abajo, nubes a la izquierda) tienen
 * que poder entrar antes de que el cull las borre.
 */
export const spawnOverscan = (preset: ParticlePreset, w: number, h: number): number => {
  const area = preset.spawnArea;
  if (!area) return 0;
  return Math.max(0, -area.x * w, -area.y * h, (area.x + area.w - 1) * w, (area.y + area.h - 1) * h);
};

/**
 * Avanza la física de una partícula. Devuelve false si murió (edad o salió
 * del área con margen = max(su tamaño, overscan de la emisión), así las nubes
 * grandes no desaparecen de golpe en el borde ni la nieve muere al nacer).
 */
export const stepParticle = (
  p: Particle,
  preset: ParticlePreset,
  w: number,
  h: number,
  dt: number,
  dragF: number,
  gravStep: number
): boolean => {
  p.vx *= dragF;
  p.vy = p.vy * dragF + gravStep;
  p.x += p.vx * dt;
  p.y += p.vy * dt;
  if (preset.sway) {
    p.x += Math.sin(p.age * 0.05 + p.phase) * preset.sway * dt;
  }
  p.rot += p.vrot * dt;
  p.age += dt;
  // Las que nacen fuera del borde pueden alejarse un poco antes de entrar
  // (lluvia diagonal): margen = overscan + unos frames a velocidad máxima.
  const margin = Math.max(30, p.size, spawnOverscan(preset, w, h) + preset.speedMax * 3);
  return !(p.age > p.ttl || p.y > h + margin || p.y < -margin || p.x < -margin || p.x > w + margin);
};

/** Alpha 0..1 de la partícula según el preset (fade lineal, twinkle o bordes suaves). */
export const particleAlpha = (p: Particle, preset: ParticlePreset): number => {
  const max = preset.alphaMax ?? 1;
  const life = Math.min(1, p.age / p.ttl);
  if (preset.twinkle) {
    const envelope = Math.min(1, life * 6, (1 - life) * 6);
    return max * envelope * (0.35 + 0.65 * Math.abs(Math.sin(p.age * 0.08 + p.phase)));
  }
  if (preset.softEdges) {
    return max * Math.max(0, Math.min(1, life / 0.15, (1 - life) / 0.25));
  }
  return max * Math.max(0, 1 - life);
};

let sparkTextureCache: HTMLCanvasElement | null = null;

/**
 * Textura radial blanca (centro opaco → borde transparente) construida una
 * sola vez: `drawImage` con ella es ~10–20× más barato que crear un
 * `createRadialGradient` por partícula por frame.
 */
export const getSparkTexture = (): HTMLCanvasElement => {
  if (sparkTextureCache) return sparkTextureCache;
  const SIZE = 64;
  const c = document.createElement("canvas");
  c.width = SIZE;
  c.height = SIZE;
  const stCtx = c.getContext("2d");
  if (!stCtx) return c;
  const grad = stCtx.createRadialGradient(SIZE / 2, SIZE / 2, 0, SIZE / 2, SIZE / 2, SIZE / 2);
  grad.addColorStop(0, "rgba(255,255,255,1)");
  grad.addColorStop(0.4, "rgba(255,255,255,0.6)");
  grad.addColorStop(1, "rgba(255,255,255,0)");
  stCtx.fillStyle = grad;
  stCtx.fillRect(0, 0, SIZE, SIZE);
  sparkTextureCache = c;
  return c;
};

const drawHeart = (ctx: CanvasRenderingContext2D, x: number, y: number, size: number) => {
  const s = size / 16;
  ctx.beginPath();
  ctx.moveTo(x, y + 4 * s);
  ctx.bezierCurveTo(x, y, x - 8 * s, y, x - 8 * s, y + 4 * s);
  ctx.bezierCurveTo(x - 8 * s, y + 8 * s, x, y + 12 * s, x, y + 14 * s);
  ctx.bezierCurveTo(x, y + 12 * s, x + 8 * s, y + 8 * s, x + 8 * s, y + 4 * s);
  ctx.bezierCurveTo(x + 8 * s, y, x, y, x, y + 4 * s);
  ctx.closePath();
  ctx.fill();
};

const drawCoin = (
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  size: number,
  rot: number
) => {
  // Elipse acortada girando sobre su eje vertical: sugiere una moneda 3D.
  const w = Math.abs(Math.cos(rot)) * size + 2;
  ctx.beginPath();
  ctx.ellipse(x, y, w / 2, size / 2, 0, 0, Math.PI * 2);
  ctx.fill();
  ctx.strokeStyle = "rgba(0,0,0,0.4)";
  ctx.lineWidth = 1;
  ctx.stroke();
};

const drawSpark = (
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  size: number,
  texture: HTMLCanvasElement
) => {
  // Núcleo de color (fillStyle del caller) + halo blanco aditivo encima.
  ctx.beginPath();
  ctx.arc(x, y, size * 0.8, 0, Math.PI * 2);
  ctx.fill();
  const prev = ctx.globalCompositeOperation;
  ctx.globalCompositeOperation = "lighter";
  const s = size * 3;
  ctx.drawImage(texture, x - s / 2, y - s / 2, s, s);
  ctx.globalCompositeOperation = prev;
};

/** Trazo fino en la dirección de la velocidad (gota de lluvia). */
const drawStreak = (ctx: CanvasRenderingContext2D, p: Particle, color: string) => {
  const len = Math.hypot(p.vx, p.vy) || 1;
  const ux = p.vx / len;
  const uy = p.vy / len;
  ctx.strokeStyle = color;
  ctx.lineWidth = 1.5;
  ctx.lineCap = "round";
  ctx.beginPath();
  ctx.moveTo(p.x, p.y);
  ctx.lineTo(p.x - ux * p.size, p.y - uy * p.size);
  ctx.stroke();
};

/** Mancha suave (la textura radial tintada) — nubes/niebla. */
const drawCloud = (
  ctx: CanvasRenderingContext2D,
  p: Particle,
  color: string,
  texture: HTMLCanvasElement
) => {
  const s = p.size;
  // Tinte: disco de color muy transparente y encima la textura blanca difusa.
  ctx.fillStyle = color;
  ctx.globalAlpha *= 0.5;
  ctx.beginPath();
  ctx.arc(p.x, p.y, s * 0.35, 0, Math.PI * 2);
  ctx.fill();
  ctx.globalAlpha *= 2;
  ctx.drawImage(texture, p.x - s / 2, p.y - s * 0.3, s, s * 0.6);
};

const drawRing = (ctx: CanvasRenderingContext2D, p: Particle, color: string) => {
  ctx.strokeStyle = color;
  ctx.lineWidth = 1.5;
  ctx.beginPath();
  ctx.arc(p.x, p.y, p.size / 2, 0, Math.PI * 2);
  ctx.stroke();
  // Brillo chico en el borde superior-izquierdo, como una burbuja real.
  ctx.beginPath();
  ctx.arc(p.x - p.size * 0.18, p.y - p.size * 0.18, Math.max(1, p.size * 0.08), 0, Math.PI * 2);
  ctx.fillStyle = color;
  ctx.fill();
};

export const drawParticle = (
  ctx: CanvasRenderingContext2D,
  p: Particle,
  preset: ParticlePreset,
  color: string,
  sparkTexture: HTMLCanvasElement
): void => {
  ctx.save();
  ctx.globalAlpha = particleAlpha(p, preset);
  ctx.fillStyle = color;
  if (p.shape === "rect") {
    ctx.translate(p.x, p.y);
    ctx.rotate(p.rot);
    ctx.fillRect(-p.size / 2, -p.size / 4, p.size, p.size / 2);
  } else if (p.shape === "circle") {
    ctx.beginPath();
    ctx.arc(p.x, p.y, p.size / 2, 0, Math.PI * 2);
    ctx.fill();
  } else if (p.shape === "heart") {
    drawHeart(ctx, p.x - p.size / 2, p.y - p.size / 2, p.size);
  } else if (p.shape === "coin") {
    drawCoin(ctx, p.x, p.y, p.size, p.rot);
  } else if (p.shape === "streak") {
    drawStreak(ctx, p, color);
  } else if (p.shape === "cloud") {
    drawCloud(ctx, p, color, sparkTexture);
  } else if (p.shape === "ring") {
    drawRing(ctx, p, color);
  } else {
    drawSpark(ctx, p.x, p.y, p.size, sparkTexture);
  }
  ctx.restore();
};

export interface ParticleFrameOptions {
  preset: IOverlayParticlePreset;
  /** 0–100. Default 60. */
  intensity?: number;
  color?: string;
}

/** Color CSS con el que se pinta la partícula: el del elemento, el del preset o su hue. */
export const particleColor = (
  p: Particle,
  opts: ParticleFrameOptions,
  preset: ParticlePreset
): string => opts.color ?? preset.defaultColor ?? `hsl(${p.hue},85%,60%)`;

/**
 * La simulación de un frame sin dibujar: emite, avanza y descarta las muertas
 * (quedan en `particles` en orden de nacimiento). `dt` en unidades de frame a
 * 60 Hz (1 = 16,7 ms), capado por el caller. El carril DOM la dibuja después
 * en canvas2d (`renderParticlesFrame`); el Pixi, con sprites. Devuelve el
 * preset resuelto para que el caller pinte con él.
 */
export const stepParticlesFrame = (
  particles: Particle[],
  opts: ParticleFrameOptions,
  w: number,
  h: number,
  dt: number
): ParticlePreset => {
  const preset = PRESET_DEFAULTS[opts.preset];
  const intensity = (opts.intensity ?? 60) / 100;
  // Drag fue autorado como multiplicador por frame: se exponencia con dt.
  const dragF = Math.pow(preset.drag, dt);
  const gravStep = preset.gravity * dt;

  emitParticles(particles, preset, intensity, w, h, dt);

  for (let i = particles.length - 1; i >= 0; i -= 1) {
    if (!stepParticle(particles[i], preset, w, h, dt, dragF, gravStep)) {
      particles.splice(i, 1);
    }
  }
  return preset;
};

/**
 * Un frame completo: emite, avanza, descarta las muertas y dibuja sobre `ctx` (que ya debe
 * estar limpio y con la transform en px de diseño). Se pinta de la más nueva a
 * la más vieja (la vieja queda encima), como siempre.
 */
export const renderParticlesFrame = (
  ctx: CanvasRenderingContext2D,
  particles: Particle[],
  opts: ParticleFrameOptions,
  w: number,
  h: number,
  dt: number,
  sparkTexture: HTMLCanvasElement
): void => {
  const preset = stepParticlesFrame(particles, opts, w, h, dt);

  for (let i = particles.length - 1; i >= 0; i -= 1) {
    const p = particles[i];
    drawParticle(ctx, p, preset, particleColor(p, opts, preset), sparkTexture);
  }
};
